// Top Qualifier Dashboard Tile Component
// Shows on main dashboard for TQ-enabled users
// Provides quick access to TQ dashboard via SSO

import React, { useState } from 'react';
import { supabase } from '../../supabase/client';

interface TopQualifierTileProps {
  tqEnabled: boolean;
  tqActivatedAt: string | null;
}

const TopQualifierTile: React.FC<TopQualifierTileProps> = ({ tqEnabled, tqActivatedAt }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!tqEnabled) {
    return null; // Don't show tile if TQ not enabled
  }

  const handleOpenDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Not authenticated');
        return;
      }

      // Call SSO token generator
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-tq-sso-token`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate SSO token');
      }

      const { token, tenant } = await response.json();

      // POST token to TQ SSO validation endpoint
      const tqBaseUrl = import.meta.env.VITE_TQ_BASE_URL || 'https://top-qualifier-api.onrender.com';
      const ssoResponse = await fetch(`${tqBaseUrl}/api/sso/validate?redirect=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
        credentials: 'include', // Important: allows cookies to be set
      });

      if (!ssoResponse.ok) {
        const errorData = await ssoResponse.json();
        throw new Error(errorData.detail || 'SSO validation failed');
      }

      // TQ returns 303 redirect, so we need to navigate to the redirect location
      // The cookie (tq_session) is now set
      window.open(
        `${tqBaseUrl}/stage4/tenant/ui`,
        '_blank',
        'noopener,noreferrer'
      );

    } catch (err) {
      console.error('Error opening TQ dashboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to open dashboard');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-sm border border-purple-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            Top Qualifier AI
            <span className="ml-2 text-xl">🤖</span>
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            AI-powered lead qualification and engagement
          </p>
        </div>
      </div>

      {/* Status Badge */}
      <div className="mb-4">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
          Active
          {tqActivatedAt && (
            <span className="ml-1">
              since {new Date(tqActivatedAt).toLocaleDateString()}
            </span>
          )}
        </span>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={handleOpenDashboard}
          disabled={loading}
          className="w-full flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Opening...
            </>
          ) : (
            <>
              <span className="mr-2">🚀</span>
              Open TQ Dashboard
            </>
          )}
        </button>

        <a
          href="/settings?tab=top-qualifier"
          className="w-full flex items-center justify-center px-4 py-2 bg-white text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors text-sm font-medium"
        >
          <span className="mr-2">⚙️</span>
          TQ Settings
        </a>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Quick Info */}
      <div className="mt-4 pt-4 border-t border-purple-100">
        <p className="text-xs text-gray-500">
          💡 <span className="font-medium">Pro tip:</span> View your hosted chat link and embed code in Settings
        </p>
      </div>
    </div>
  );
};

export default TopQualifierTile;
